import { NavLink } from "react-router-dom";
import { useState } from "react";
import { useYear } from "@/context/YearContext";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Table2, CalendarCheck2, Landmark, Settings2, Menu, X, Plus, LogOut } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const tabs = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "nav-tab-dashboard" },
  { to: "/budget", label: "Budget", icon: Table2, id: "nav-tab-budget" },
  { to: "/closing", label: "Monthly Closing", icon: CalendarCheck2, id: "nav-tab-closing" },
  { to: "/funds", label: "Funds", icon: Landmark, id: "nav-tab-funds" },
  { to: "/settings", label: "Settings", icon: Settings2, id: "nav-tab-settings" },
];

export const Header = () => {
  const { year, setYear, years, addYear } = useYear();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const visibleTabs = tabs.filter((t) => t.to !== "/settings" || user?.role === "admin");

  const maxYear = Math.max(...years, 2027) + 5;
  const yearOptions = [];
  for (let y = 2027; y <= maxYear; y++) yearOptions.push(y);

  const handleYearChange = (v) => {
    if (v === "__add__") {
      const next = maxYear + 1;
      addYear(next);
      setYear(next);
    } else {
      setYear(parseInt(v));
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[#ECE9E1] bg-white/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center font-heading font-bold text-white text-sm shadow-[0_0_20px_rgba(59,130,246,0.35)]">
              HB
            </div>
            <div className="hidden sm:block">
              <p className="font-heading font-semibold text-sm text-zinc-900 leading-tight">Harkaran Boparai Studio</p>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Capital Reserve System</p>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-1">
            {visibleTabs.map(({ to, label, icon: Icon, id }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={id}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-blue-600/10 text-blue-600" : "text-zinc-500 hover:text-zinc-900 hover:bg-[#F8F7F3]"
                  }`
                }
              >
                <Icon size={15} /> {label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Select value={String(year)} onValueChange={handleYearChange}>
              <SelectTrigger data-testid="nav-year-select" className="w-[104px] h-9 bg-white border-[#E3E0D8] font-mono-num text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
                <SelectItem value="__add__">
                  <span className="flex items-center gap-1 text-blue-600"><Plus size={13} /> Add {maxYear + 1}</span>
                </SelectItem>
              </SelectContent>
            </Select>
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#E3E0D8] bg-white" data-testid="user-badge">
              <span className={`w-2 h-2 rounded-full ${user?.role === "admin" ? "bg-blue-500" : "bg-emerald-500"}`} />
              <span className="text-xs font-medium text-zinc-700 max-w-[130px] truncate">{user?.name}</span>
              <span className="text-[10px] uppercase tracking-wider text-zinc-400">{user?.role}</span>
            </div>
            <button data-testid="logout-btn" onClick={logout} title="Sign out"
              className="p-2 rounded-lg text-zinc-500 hover:text-red-600 hover:bg-red-50 transition-colors">
              <LogOut size={16} />
            </button>
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 rounded-lg text-zinc-700 hover:bg-[#F8F7F3]"
              onClick={() => setOpen(!open)}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-[#ECE9E1] bg-[#FCFBF8] px-4 py-3 space-y-1">
          {visibleTabs.map(({ to, label, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${id}-mobile`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                  isActive ? "bg-blue-600/10 text-blue-600" : "text-zinc-500"
                }`
              }
            >
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
};
